import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Refresh } from '../../../data/rest/accountUser'
import UserSession from '../../../utils/UserSession'
import {
  AccessToken,
  getEmailFromToken,
  getLoginRedirectPath,
  setAccountId
} from '../../../utils/authUtils'

export type AuthState = 'checking' | 'authenticated' | 'unauthenticated'

const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000

const getInitialAuthState = (): AuthState =>
  AccessToken.verify().valid ? 'authenticated' : 'checking'

interface AuthGuardResult {
  /** True once the user is authenticated AND the account cookies are populated. */
  ready: boolean
  authState: AuthState
}

/**
 * Tri-state auth guard. Mirrors fd-manager's `auth-guard.tsx` so the two
 * apps converge on the same session-recovery semantics:
 *
 *   - `authenticated`: sync token check passed → render immediately.
 *   - `checking`: sync check failed; attempt a refresh via the httpOnly
 *     refresh_token cookie. On success → `authenticated`; on failure →
 *     `unauthenticated`.
 *   - `unauthenticated`: redirect to /login.
 *
 * Also bootstraps the `fd_account_id` and `fd_account_name` cookies when
 * a user arrives with a valid token but no account context (typical for
 * cross-app SSO via fd-manager). Falls back to the email claim on the
 * access token JWT when `fd_email` isn't on the parent domain, and
 * backfills the cookie so subsequent loads hit the cookie path.
 */
export const useAuthGuard = (): AuthGuardResult => {
  const navigate = useNavigate()
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState)
  const [hasAccountId, setHasAccountId] = useState<boolean>(
    UserSession.maybeGetAccountId() !== null
  )

  // Attempt refresh when token is missing or expired.
  useEffect(() => {
    if (authState !== 'checking') return
    let cancelled = false
    Refresh()
      .then((response) => {
        if (cancelled) return
        AccessToken.set(response.data.accessToken)
        setAuthState('authenticated')
      })
      .catch(() => {
        if (!cancelled) setAuthState('unauthenticated')
      })
    return () => {
      cancelled = true
    }
  }, [authState])

  // Redirect to login once we've determined the session can't be recovered.
  useEffect(() => {
    if (authState !== 'unauthenticated') return
    navigate(getLoginRedirectPath())
  }, [authState, navigate])

  // One-shot refresh when first authenticated, then periodic refreshes
  // every 15 minutes. The mount-time refresh matches the previous
  // RefreshTokenWrapper behaviour — without it, a user landing with a
  // sync-valid token that has fewer than 15 minutes of life left could
  // see it expire before the periodic timer fires (the Apollo retry link
  // catches GraphQL 401s, but plain REST calls have no equivalent
  // safety net). The interval is cleared on unmount or on leaving the
  // authenticated state so the timer doesn't leak across remounts.
  useEffect(() => {
    if (authState !== 'authenticated') return
    let cancelled = false
    const refresh = () => {
      Refresh()
        .then((response) => {
          if (!cancelled) AccessToken.set(response.data.accessToken)
        })
        .catch(() => {
          if (!cancelled) setAuthState('unauthenticated')
        })
    }
    refresh()
    const id = setInterval(refresh, TOKEN_REFRESH_INTERVAL)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [authState])

  // Bootstrap account cookies when authenticated but no fd_account_id.
  useEffect(() => {
    if (authState !== 'authenticated' || hasAccountId) return
    const { token } = AccessToken.verify()
    if (!token) {
      setAuthState('unauthenticated')
      return
    }
    const cookieEmail = UserSession.getEmail()
    const email = cookieEmail ?? getEmailFromToken(token)
    if (!email) {
      setAuthState('unauthenticated')
      return
    }
    if (!cookieEmail) {
      UserSession.setEmail(email)
    }
    let cancelled = false
    setAccountId(email, token)
      .then((ok) => {
        if (cancelled) return
        if (ok) {
          setHasAccountId(true)
        } else {
          // setAccountId resolves false when GetAccountsByEmail returns no
          // accountId — nothing to retry, so treat the session as
          // unrecoverable rather than rendering null forever.
          setAuthState('unauthenticated')
        }
      })
      .catch(() => {
        if (!cancelled) setAuthState('unauthenticated')
      })
    return () => {
      cancelled = true
    }
  }, [authState, hasAccountId])

  return {
    ready: authState === 'authenticated' && hasAccountId,
    authState
  }
}
